import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Heart, Users } from 'lucide-react';
import { getApiBase } from '../utils/apiBase';

const API = getApiBase();

const cardShell =
  'w-full max-w-md border border-[#e8e2d8]/90 bg-[#fffcf7]/95 shadow-[0_1px_0_rgba(28,25,23,0.04),0_12px_40px_-24px_rgba(28,25,23,0.12)] rounded-2xl';

const AcceptFamilySpaceInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const acceptInvitation = useCallback(async () => {
    try {
      await axios.post(`${API}/family-spaces/invitations/accept`, { token });
      setSuccess(true);
      toast.success('You joined the family space');
      setTimeout(() => {
        navigate('/family-spaces', { replace: true });
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'This invitation may have expired or already been used.');
      toast.error(err.response?.data?.detail || 'Unable to accept invitation');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (user && token) {
      acceptInvitation();
    } else if (!user) {
      setLoading(false);
    }
  }, [user, token, acceptInvitation]);

  if (!user) {
    return (
      <div className="relative min-h-screen bg-[#faf8f5]">
        <div className="noise-overlay pointer-events-none fixed inset-0 z-0 opacity-70" aria-hidden />
        <div className="relative z-10 flex min-h-screen items-center justify-center p-4 sm:p-6">
          <Card className={cardShell}>
            <CardHeader className="pb-2 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#c9a961]/18 ring-1 ring-[#c9a961]/25">
                <Heart className="h-8 w-8 text-[#8b7355]" aria-hidden />
              </div>
              <CardTitle className="font-serif text-2xl font-normal text-[#1c1917]">
                Family space invitation
              </CardTitle>
              <CardDescription className="text-base leading-relaxed text-[#5c534a]">
                Sign in or create an account with the same email this invitation was sent to—then you can join the space.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
              <Button
                onClick={() => navigate('/login', { state: { returnTo: `/accept-family-space-invite/${token}` } })}
                className="h-12 w-full rounded-full bg-[#1c1917] text-[#faf7f2] hover:bg-[#2c2824]"
              >
                Sign in
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/register', { state: { returnTo: `/accept-family-space-invite/${token}` } })}
                className="h-12 w-full rounded-full border-[#c9a961]/40 bg-transparent text-[#1c1917] hover:bg-[#faf8f5]"
              >
                Create account
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#faf8f5]">
      <div className="noise-overlay pointer-events-none fixed inset-0 z-0 opacity-70" aria-hidden />
      <div className="relative z-10 flex min-h-screen items-center justify-center p-4 sm:p-6">
        <Card className={cardShell}>
          <CardHeader className="pb-2 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-[#8b7355]">Family space</p>
            <CardTitle className="mt-2 font-serif text-xl font-normal text-[#1c1917]">Joining…</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && (
              <div className="flex flex-col items-center py-12">
                <Loader2 className="mb-4 h-11 w-11 animate-spin text-[#8b7355]" aria-hidden />
                <p className="text-sm text-[#5c534a]">Adding you to the space…</p>
              </div>
            )}
            {success && (
              <div className="flex flex-col items-center py-12">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#c9a961]/20 ring-1 ring-[#c9a961]/30">
                  <CheckCircle2 className="h-9 w-9 text-[#5c4a32]" aria-hidden />
                </div>
                <p className="mb-2 font-serif text-lg font-medium text-[#1c1917]">You&apos;re in</p>
                <p className="max-w-xs text-center text-sm leading-relaxed text-[#5c534a]">
                  Redirecting to family spaces…
                </p>
              </div>
            )}
            {error && (
              <div className="py-10 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#ebe6dc]">
                  <Users className="h-8 w-8 text-[#68645e]" aria-hidden />
                </div>
                <p className="mb-2 font-medium text-[#1c1917]">We couldn&apos;t complete that</p>
                <p className="mb-6 text-sm leading-relaxed text-[#5c534a]">{error}</p>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="rounded-full bg-[#1c1917] px-8 text-[#faf7f2] hover:bg-[#2c2824]"
                >
                  Go to dashboard
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AcceptFamilySpaceInvite;
